import React, { useState } from 'react';
import {
  FaMoneyBillWave, FaUser, FaKey, FaExclamationTriangle,
  FaShieldAlt, FaArrowLeft, FaSave, FaRupeeSign,
  FaUniversity, FaIdCard, FaLock, FaSpinner
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ReporterWithdrawalRequest = () => {
  const [form, setForm] = useState({
    accountHolder: '',
    accountNumber: '',
    ifsc: '',
    bankName: '',
    amount: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case 'accountHolder':
        if (!value.trim()) return 'Required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Alphabets only';
        return '';
      case 'accountNumber':
        if (!value.trim()) return 'Required';
        if (!/^\d+$/.test(value)) return 'Digits only';
        if (value.length < 9 || value.length > 18) return '9-18 digits';
        return '';
      case 'ifsc':
        if (!value.trim()) return 'Required';
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) return 'Invalid format';
        return '';
      case 'bankName':
        if (!value.trim()) return 'Required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Alphabets only';
        return '';
      case 'amount':
        if (!value.trim()) return 'Required';
        const amt = parseFloat(value);
        if (isNaN(amt) || amt <= 0) return 'Invalid amount';
        if (amt > 100000) return 'Max ₹1,00,000';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'accountHolder' || name === 'bankName') formattedValue = value.replace(/[^A-Za-z\s]/g, '');
    if (name === 'accountNumber') formattedValue = value.replace(/[^0-9]/g, '');
    if (name === 'ifsc') formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (name === 'amount') formattedValue = value.replace(/[^0-9.]/g, '');

    setForm({ ...form, [name]: formattedValue });
    setErrors({ ...errors, [name]: validateField(name, formattedValue) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(form).forEach(key => {
      newErrors[key] = validateField(key, form[key]);
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(err => err !== '')) {
      toast.error('Please fix errors');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/withdrawal/request`,
        form,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        toast.success('Withdrawal request sent!');
        setForm({ accountHolder: '', accountNumber: '', ifsc: '', bankName: '', amount: '' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (name) => `w-full pl-9 pr-4 py-2 text-sm border ${errors[name] ? 'border-red-500 bg-red-50' : 'border-slate-200'} rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all`;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4 pt-6 pb-20 text-white">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <FaMoneyBillWave className="w-4 h-4 text-indigo-300" />
          </div>
          <div>
            <h1 className="text-base font-black text-white">Withdrawal Request</h1>
            <p className="text-indigo-300 text-[10px] font-bold uppercase tracking-widest">Reporter Wallet</p>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-3 -mt-12 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            <div className="flex-1 px-4 py-3 border-r border-slate-100 text-center">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Limit</p>
              <p className="text-xs font-black text-slate-700">₹1,00,000</p>
            </div>
            <div className="flex-1 px-4 py-3 text-center">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Status</p>
              <p className="text-xs font-black text-emerald-600 uppercase tracking-widest">Active</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="space-y-3">
              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Account Holder</label>
                <div className="absolute left-3 top-[34px] text-slate-400">
                  <FaUser className="w-3.5 h-3.5" />
                </div>
                <input name="accountHolder" value={form.accountHolder} onChange={handleChange} className={inputCls('accountHolder')} placeholder="Account name" required />
                {errors.accountHolder && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.accountHolder}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Account No.</label>
                  <div className="absolute left-3 top-[34px] text-slate-400">
                    <FaIdCard className="w-3.5 h-3.5" />
                  </div>
                  <input name="accountNumber" value={form.accountNumber} onChange={handleChange} className={inputCls('accountNumber')} placeholder="9-18 digits" maxLength="18" required />
                  {errors.accountNumber && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.accountNumber}</p>}
                </div>
                <div className="relative">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">IFSC</label>
                  <div className="absolute left-3 top-[34px] text-slate-400">
                    <FaKey className="w-3.5 h-3.5" />
                  </div>
                  <input name="ifsc" value={form.ifsc} onChange={handleChange} className={inputCls('ifsc')} placeholder="IFSC Code" maxLength="11" required />
                  {errors.ifsc && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.ifsc}</p>}
                </div>
              </div>

              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Bank Name</label>
                <div className="absolute left-3 top-[34px] text-slate-400">
                  <FaUniversity className="w-3.5 h-3.5" />
                </div>
                <input name="bankName" value={form.bankName} onChange={handleChange} className={inputCls('bankName')} placeholder="Bank name" required />
                {errors.bankName && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.bankName}</p>}
              </div>
            </div>

            <div className="h-px bg-slate-100 my-2" />

            <div className="relative">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Amount</label>
              <div className="absolute left-3 top-[34px] text-slate-400">
                <FaRupeeSign className="w-3.5 h-3.5" />
              </div>
              <input name="amount" value={form.amount} onChange={handleChange} className={inputCls('amount')} placeholder="Enter amount" required />
              {errors.amount && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.amount}</p>}
            </div>

            <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl text-sm font-black transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 disabled:opacity-50">
              {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
              {loading ? 'Processing...' : 'Submit Request'}
            </button>
          </form>

          <div className="px-5 py-4 bg-slate-50/60 border-t border-slate-100">
            <div className="flex items-start gap-2">
              <FaShieldAlt className="w-3 h-3 text-green-500 mt-0.5" />
              <p className="text-[10px] text-slate-500 font-bold">Secure Bank Transfer via encrypted gateway. Funds will be credited in 2-3 business days.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporterWithdrawalRequest;
